/* eslint-disable react-hooks/exhaustive-deps */

import { PageChangeDetails } from 'node_modules/@chakra-ui/react/dist/types/components/pagination/namespace';
import { Box, Button, Flex, Image, Stack, Table, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import emptyJobImage from '@/assets/image/empty-job-cover.svg';
import { fetchAPI } from '@/api/fetch';
import { apiGetClients, GetClientsResponse } from '@/api/services';
import { SkeletonText } from '@/components/ui/skeleton';
import {
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';
import { SearchBox } from '@/components/ui/searchbox';
import { Stepper } from '@/types/enums';
import { useCreateJob } from '@/store/createJob';

import AddNewClient from './AddNewClient';

const PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 500;
const TABLE_HEADERS = ['Client ID', 'Client Name', 'Industry'] as const;
const CLIENT_FIELDS = ['unique_id', 'name', 'industry'] as const;

interface ClientTableProps {
  clients: Client[];
  isLoading: boolean;
  selectedClientId?: string;
  onSelectClient: (client: Client) => void;
}

const ClientTable: React.FC<ClientTableProps> = ({
  clients,
  isLoading,
  selectedClientId,
  onSelectClient
}) => {
  if (isLoading) {
    return (
      <Table.Row key={1} bg="transparent">
        {CLIENT_FIELDS.map((_, index) => (
          <Table.Cell key={index} fontSize="16px">
            <SkeletonText h="24px" noOfLines={3} />
          </Table.Cell>
        ))}
      </Table.Row>
    );
  }

  return (
    <>
      {clients.map((client) => (
        <Table.Row
          key={client.client_id}
          bg={client.client_id === selectedClientId ? 'primary.100' : 'transparent'}
          _hover={{ cursor: 'pointer', bg: 'neutrals.50' }}
          onClick={() => onSelectClient(client)}
        >
          {CLIENT_FIELDS.map((field) => (
            <Table.Cell key={field} fontSize="16px">
              {client[field as keyof Client]}
            </Table.Cell>
          ))}
        </Table.Row>
      ))}
    </>
  );
};

export default function SearchClient() {
  const createJobStore = useCreateJob();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [emptyClient, setEmptyClient] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = async (page: number, keyword?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchAPI<GetClientsResponse>({
        request: () =>
          apiGetClients({
            page,
            limit: PAGE_SIZE,
            keyword
          })
      });

      if (response.success) {
        setClients(response.data.clients);
        setCurrentPage(response.data.current_page);
        setTotalPage(response.data.total_page);
        setEmptyClient(page === 1 && response.data.clients.length === 0);
      } else {
        setError('Failed to fetch clients. Please try again.');
        console.error('Failed to fetch clients:', response.message);
      }
    } catch (error) {
      setError('Failed to fetch clients. Please try again.');
      console.error('Failed to fetch clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(currentPage);
  }, [currentPage]);

  const handleSelectClient = (client: Client) => {
    createJobStore.setSelectedClient(client);
  };

  const handleNext = () => {
    if (!createJobStore.isStepEnabled(Stepper.JobDetails)) {
      createJobStore.setStep(Stepper.JobDetails);
    }
    createJobStore.setCurrentPage(Stepper.JobDetails);
  };

  const handleBack = () => {
    createJobStore.reset();
    navigate('/job');
  };

  const onAddClient = () => {
    fetchClients(1);
  };

  const searchClient = useCallback(
    debounce(async (keyword: string) => {
      await fetchClients(1, keyword);
    }, DEBOUNCE_DELAY),
    []
  );

  const changeClientPage = (details: PageChangeDetails) => {
    setCurrentPage(details.page);
  };

  return (
    <>
      <Box
        w="100%"
        border="2px solid"
        borderRadius="12px"
        borderColor="neutrals.300"
        bg="background.secondary"
      >
        <Box display="flex" flexDirection="column" p="32px">
          <Text fontWeight="bold" fontSize="36px">
            Search for a client
          </Text>
          <Text color="neutrals.500" mb="40px">
            Find an existing client or create a new client profile to proceed.
          </Text>
          {error && (
            <Text color="error.500" mb="16px">
              {error}
            </Text>
          )}
          {emptyClient ? (
            <Flex flexDir="column" alignItems="center" justifyContent="center">
              <Image src={emptyJobImage} h="280px" w="280px" mt="64px" />
              <Text fontSize="20px" fontWeight="bold" color="neutrals.700" mt="16px">
                No clients yet
              </Text>
              <Text fontSize="16px" color="neutrals.500" mb="16px">
                Add a client profile to link it to your job listings.
              </Text>
              <AddNewClient filled onAddClient={onAddClient} disabled={isLoading} />
            </Flex>
          ) : (
            <>
              <Flex justifyContent="space-between" alignItems="center" mb="24px">
                <SearchBox
                  placeholder="Search client name"
                  search={searchClient}
                  disabled={isLoading}
                />
                <AddNewClient filled={false} onAddClient={onAddClient} disabled={isLoading} />
              </Flex>
              <Table.Root size="sm" variant="line" rounded="8px" interactive>
                <Table.Header>
                  <Table.Row bg="neutrals.100">
                    {TABLE_HEADERS.map((header) => (
                      <Table.ColumnHeader
                        key={header}
                        fontSize="14px"
                        fontWeight="bold"
                        borderBottom="1px solid"
                        borderColor="neutrals.300"
                      >
                        {header}
                      </Table.ColumnHeader>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <ClientTable
                    clients={clients}
                    isLoading={isLoading}
                    selectedClientId={createJobStore.selectedClient?.client_id}
                    onSelectClient={handleSelectClient}
                  />
                </Table.Body>
              </Table.Root>
              <Flex justifyContent="flex-end" alignItems="center" mt="16px">
                <PaginationRoot
                  count={totalPage * PAGE_SIZE}
                  pageSize={PAGE_SIZE}
                  page={currentPage}
                  onPageChange={changeClientPage}
                >
                  <Stack direction="row" gap="8px">
                    <PaginationPrevTrigger />
                    <Text fontSize="14px" color="neutrals.700" alignSelf="center">
                      {currentPage} / {totalPage}
                    </Text>
                    <PaginationNextTrigger />
                  </Stack>
                </PaginationRoot>
              </Flex>
            </>
          )}
        </Box>
      </Box>

      <Box h="64px" />
      <Flex justifyContent="flex-end">
        <Button
          w="120px"
          h="48px"
          borderRadius="8px"
          borderColor="primary.900"
          color="primary.900"
          bg="background.secondary"
          fontSize="16px"
          fontWeight="regular"
          mr="16px"
          onClick={handleBack}
        >
          Cancel
        </Button>
        <Button
          disabled={!createJobStore.selectedClient || isLoading}
          w="120px"
          h="48px"
          borderRadius="8px"
          bg="primary.900"
          fontSize="16px"
          fontWeight="regular"
          onClick={handleNext}
        >
          Next
        </Button>
      </Flex>
    </>
  );
}
